import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";

import {
  StakeholdersAPI,
  CreateStakeholdersRequest,
  CreateStakeholdersResponse,
  UpdateStakeholderRequest,
  EnrichedStakeholder,
} from "../../../api/stakeholders";

import {
  StakeholderRelationshipsAPI,
  StakeholderRelationship,
  StakeholderRelationshipFields,
} from "../../../api/stakeholder_relationships";

import {
  CustomerDetails,
  UpsertStakeholderKycDetailsRequest,
  StakeholderContactDetail,
  BusinessDetails,
  BusinessKeyPersonnelDetails,
  EmployeeDetails,
  StakeholderDetailsAPI,
} from "../../../api/enrichedStakeholder";

import { MessageResponse } from "../../../api/common";

export interface FetchEnrichedStakeholderParams {
  caseId: string;
  stakeholderIds: string[];
}

export const fetchEnrichedStakeholder = createAsyncThunk<
  EnrichedStakeholder[] | null,
  FetchEnrichedStakeholderParams,
  {
    state: RootState;
    rejectValue: string;
  }
>(
  "stakeholder/fetchEnrichedStakeholder",
  async (
    { caseId, stakeholderIds },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState();
      
      const missingStakeholders = stakeholderIds.filter(id => !(id in state.stakeholders.enrichedStakeholders.entities)); 

      if (missingStakeholders.length === 0) {
        return null;
      }

      const response = await StakeholdersAPI.get(
        caseId,
        missingStakeholders,
      );

      return response.stakeholders;
    } catch {
      return rejectWithValue(
        "Failed to fetch stakeholder details",
      );
    }
  },
);

export interface CreateStakeholdersParams {
  caseId: string;
  request: CreateStakeholdersRequest;
}

export const createStakeholders = createAsyncThunk<
  CreateStakeholdersResponse,
  CreateStakeholdersParams,
  { rejectValue: string }
>(
  "stakeholder/createStakeholders",
  async (
    { caseId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholdersAPI.add(
        caseId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to create stakeholders",
      );
    }
  },
);

export interface UpdateStakeholderParams {
  caseId: string;
  stakeholderId: string;
  request: UpdateStakeholderRequest;
}

export const updateStakeholder = createAsyncThunk<
  MessageResponse,
  UpdateStakeholderParams,
  { rejectValue: string }
>(
  "stakeholder/updateStakeholder",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholdersAPI.update(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder",
      );
    }
  },
);

export interface CreateStakeholderRelationshipParams {
  caseId: string;
  stakeholderId: string;
  request: StakeholderRelationshipFields;
}

export const createStakeholderRelationship = createAsyncThunk<
  StakeholderRelationship,
  CreateStakeholderRelationshipParams,
  { rejectValue: string }
>(
  "stakeholder/createStakeholderRelationship",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderRelationshipsAPI.add(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to create stakeholder relationship",
      );
    }
  },
);

export interface UpdateCustomerDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: CustomerDetails;
}

export const updateCustomerDetails = createAsyncThunk<
  MessageResponse,
  UpdateCustomerDetailsParams,
  { rejectValue: string }
>(
  "stakeholder/updateCustomerDetails",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderDetailsAPI.updateCustomerDetails(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder customer details",
      );
    }
  },
);

export interface UpdateStakeholderKycDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: UpsertStakeholderKycDetailsRequest;
}

export const updateStakeholderKycDetails = createAsyncThunk<
  MessageResponse,
  UpdateStakeholderKycDetailsParams,
  { rejectValue: string }
>(
  "stakeholder/updateStakeholderKycDetails",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderDetailsAPI.updateKycDetails(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder KYC details",
      );
    }
  },
);

export interface UpdateStakeholderContactDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: StakeholderContactDetail;
}

export const updateStakeholderContactDetails = createAsyncThunk<
  MessageResponse,
  UpdateStakeholderContactDetailsParams,
  { rejectValue: string }
>(
  "stakeholder/updateStakeholderContactDetails",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderDetailsAPI.updateContactDetails(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder contact details",
      );
    }
  },
);

export interface UpdateBusinessKeyPersonnelDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: BusinessKeyPersonnelDetails;
}

export const updateBusinessKeyPersonnelDetails =
  createAsyncThunk<
    MessageResponse,
    UpdateBusinessKeyPersonnelDetailsParams,
    { rejectValue: string }
  >(
    "stakeholder/updateBusinessKeyPersonnelDetails",
    async (
      { caseId, stakeholderId, request },
      { rejectWithValue },
    ) => {
      try {
        return await StakeholderDetailsAPI.updateBusinessKeyPersonnelDetails(
          caseId,
          stakeholderId,
          request,
        );
      } catch {
        return rejectWithValue(
          "Failed to update stakeholder business key personnel details",
        );
      }
    },
  );

export interface UpdateBusinessDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: BusinessDetails;
}

export const updateBusinessDetails = createAsyncThunk<
  MessageResponse,
  UpdateBusinessDetailsParams,
  { rejectValue: string }
>(
  "stakeholder/updateBusinessDetails",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderDetailsAPI.updateBusinessDetails(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder business details",
      );
    }
  },
);

export interface UpdateEmployeeDetailsParams {
  caseId: string;
  stakeholderId: string;
  request: EmployeeDetails;
}

export const updateEmployeeDetails = createAsyncThunk<
  MessageResponse,
  UpdateEmployeeDetailsParams,
  { rejectValue: string }
>(
  "stakeholder/updateEmployeeDetails",
  async (
    { caseId, stakeholderId, request },
    { rejectWithValue },
  ) => {
    try {
      return await StakeholderDetailsAPI.updateEmployeeDetails(
        caseId,
        stakeholderId,
        request,
      );
    } catch {
      return rejectWithValue(
        "Failed to update stakeholder employee details",
      );
    }
  },
);